import Card from '@pelckmans/elementis/components/card';
import MaterialBundlesIcon from '@pelckmans/elementis/icons/material-bundles';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SharedUserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import { useSlideSetAnalytics } from '../../../../../hooks/useSlideSetAnalytics';
import IdentityContext from '../../../../../contexts/IdentityContext';
import ModalContext from '../../../../../contexts/ModalContext';
import { AssignmentDeadlinePill } from '../../../../../shared-components/AssignmentDeadlinePill';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { buildDoceoUrl } from '../../../../../utils/slideSet';
import ModuleContext from '../../../_contexts/ModuleContext';
import { MaterialIcon } from '../../../components/MaterialIcon';
import MedialinksContext from '../../_contexts/MedialinksContext';
import PinnedMaterialContext, { type PinnedMaterial } from '../../_contexts/PinnedMaterialContext';
import SharedMaterialsContext from '../../_contexts/SharedMaterialsContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import useMedialinks from '../../hooks/medialink/useMedialinks';
import useUserMaterial from '../../hooks/user-material/useUserMaterial';
import MaterialPills from '../MaterialPills';
import ScorableProgress from '../ScorableProgress';
import { isScorableMedialink } from '../../../../../api/medialink';
import AccountContext from '../../../../../contexts/AccountContext';

interface PinnedMaterialCardProps {
  material: PinnedMaterial;
  /** Greys the card out and makes it inert. The pin control stays interactive. */
  disabled?: boolean;
  /** Shows the pin control, letting the user unpin the material from this section. */
  pinnable?: boolean;
}

export default function PinnedMaterialCard({ material, disabled, pinnable }: PinnedMaterialCardProps) {
  const { t } = useTranslation();
  const { identity } = useContext(IdentityContext);
  const {
    account: { provider },
  } = useContext(AccountContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode, nodes } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);

  const { pinnedMaterialList, pinMaterials, unpinMaterials } = useContext(PinnedMaterialContext);
  const [isPinSubmitting, setIsPinSubmitting] = useState(false);

  const { medialinks, fetchMedialinks } = useContext(MedialinksContext);
  const { openMedialink } = useMedialinks();

  const { userMaterial, fetchUserMaterial } = useContext(UserMaterialContext);
  const { sharedMaterials } = useContext(SharedMaterialsContext);
  const { openMaterial: openUserMaterial } = useUserMaterial();

  const { captureOpened } = useSlideSetAnalytics();

  const subLocationId = selectedModule?.isExerciseModule
    ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_PINNED_MATERIAL
    : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_PINNED_MATERIAL;

  function getContentType() {
    if (material.deadline && !identity.isTeacherAlike) {
      return t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.ASSIGNMENT');
    } else if (material.type === 'user-material') {
      return identity.isTeacherAlike
        ? t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.OWN_MATERIAL')
        : t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SHARED_USER_MATERIAL');
    } else if (material.type === 'slide-set') {
      if (material.isUserSlideSet) {
        return identity.isTeacherAlike
          ? t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.OWN_SLIDE_SETS')
          : t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SHARED_USER_SLIDE_SETS');
      }
      return t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SLIDE_SET');
    } else {
      return material.contentType?.name;
    }
  }

  function getTags() {
    const medialink = medialinks.find(x => x.id === material.id);
    const isAdaptive = medialink?.assessmentQExercise?.isAdaptive;
    const isOnTheFly = medialink?.assessmentQExercise?.isOnTheFly;

    if (identity.isTeacherAlike) {
      return buildGroupProps(
        <MaterialPills
          isAssignment={material.isAssignment}
          isShared={material.isShared}
          isStudent={material.group === 'student'}
          isTeacher={material.group === 'teacher'}
          isAdaptive={isAdaptive}
          isOnTheFly={isOnTheFly}
        />,
      );
    }

    return buildGroupProps(
      <>
        {material.deadline && <AssignmentDeadlinePill deadline={material.deadline} key="assignment-deadline-pill" />}
        <MaterialPills isAdaptive={isAdaptive} isOnTheFly={isOnTheFly} key="material-pills" />
      </>,
    );
  }

  const isPinned = pinnedMaterialList.some(m => m.id === material.id);

  const handlePinChange = async (nextPinned: boolean) => {
    if (isPinSubmitting) return;
    setIsPinSubmitting(true);

    const target = [{ materialId: material.id, materialType: material.type }];
    if (nextPinned) {
      await pinMaterials(target);
    } else {
      await unpinMaterials(target);
    }

    setIsPinSubmitting(false);
  };

  const pin = pinnable
    ? {
        pinned: isPinned,
        onPinChange: handlePinChange,
        label: t(
          isPinned
            ? 'PAGES.TOC_LEVEL.PINNED_MATERIAL.BUTTON.A11Y_LABEL_UNPIN'
            : 'PAGES.TOC_LEVEL.PINNED_MATERIAL.BUTTON.A11Y_LABEL_PIN',
        ),
      }
    : undefined;

  if (material.type === 'slide-set') {
    const onSlideSetClick = () => {
      captureOpened(Number(material.id), subLocationId);
    };

    return (
      <Card
        titleText={material.name}
        titleTag="h3"
        as="link"
        disabled={disabled}
        pin={pin}
        icon={<MaterialIcon materialType="slide-set" isUserSlideSet={material.isUserSlideSet} />}
        iconText={getContentType()}
        tags={getTags()}
        href={buildDoceoUrl({ ...material, id: Number(material.id) }, provider)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onSlideSetClick}
      />
    );
  }

  async function handleClickMaterial() {
    if (!selectedNode || !selectedModule) return;

    if (material.type === 'medialink') {
      const medialink = medialinks.find(x => x.id === material.id);

      if (medialink) {
        const modal = await openMedialink(
          medialink,
          selectedNode,
          selectedModule,
          isScorableMedialink(medialink) ? fetchMedialinks : undefined,
          undefined,
          subLocationId,
          // The href branch below renders an anchor that already navigates.
          Boolean(material.href),
        );

        if (modal) {
          showModal(modal);
        }
      }
    }

    if (material.type === 'user-material') {
      const foundUserMaterial = userMaterial.find(x => x.id === material.id);
      const foundSharedMaterial = sharedMaterials.find(x => x.id === material.id) as SharedUserMaterial;
      const foundMaterial = foundUserMaterial ?? foundSharedMaterial;

      if (foundMaterial) {
        const modal = await openUserMaterial(
          foundMaterial,
          nodes,
          foundMaterial.userMaxScore ? fetchUserMaterial : undefined,
          subLocationId,
        );
        if (modal) showModal(modal);
      }
    }
  }

  const icon = material.isBundle ? (
    <MaterialBundlesIcon data-testid="icon-bundle" />
  ) : (
    <MaterialIcon materialType={material.type} contentType={material.contentType} />
  );

  // logic for non-embedded links is handled in the PinnedMaterialContext
  if (material.href) {
    return (
      <Card
        as="link"
        href={material.href}
        target="_blank"
        disabled={disabled}
        pin={pin}
        rel="noopener noreferrer"
        titleText={material.name}
        titleTag="h3"
        onClick={() => handleClickMaterial()}
        icon={icon}
        iconText={getContentType()}
        tags={getTags()}
      />
    );
  }

  return (
    <Card
      as="button"
      titleText={material.name}
      titleTag="h3"
      disabled={disabled}
      pin={pin}
      onClick={handleClickMaterial}
      icon={icon}
      iconText={getContentType()}
      tags={getTags()}
      results={
        material.score && (
          <ScorableProgress unachievable={material.unachievable} score={material.score} variant="donut" />
        )
      }
    />
  );
}
